import React from 'react'
  import { MDXTag } from '@mdx-js/tag'


  const layoutProps = {};
export default class MDXContent extends React.Component {
  constructor(props) {
    super(props);
    this.layout = null;
  }

  render() {
    const {
      components,
      ...props
    } = this.props;
    return <MDXTag name="wrapper" components={components}><MDXTag name="h1" components={components}>{`Extending`}</MDXTag>
      <MDXTag name="p" components={components}>{`Rebass components are great to use as base components that can be extended for various purposes in your design system.`}</MDXTag>
      <MDXTag name="p" components={components}>{`There are two primary ways to extend Rebass components:`}</MDXTag>
      <MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{
          "className": "language-jsx"
        }}>{`// React-based extension
import React from 'react'
import { Box } from 'rebass'

const Container = props =>
  <Box
    {...props}
    mx='auto'
    css={{
      maxWidth: '1024px'
    }}
  />
`}</MDXTag></MDXTag>
      <MDXTag name="p" components={components}>{`Using the `}<MDXTag name="inlineCode" components={components} parentName="p">{`css`}</MDXTag>{` prop requires the use of `}<MDXTag name="a" components={components} parentName="p" props={{
          "href": "https://www.styled-components.com/docs/api#css-prop"
        }}>{`babel-plugin-styled-components`}</MDXTag></MDXTag>
      <MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{
          "className": "language-js"
        }}>{`// styled-components based extension
import styled from 'styled-components'
import { Box } from 'rebass'

const Container = styled(Box)({
  maxWidth: '1024px'
})

Container.defaultProps = {
  mx: 'auto'
}
`}</MDXTag></MDXTag>
      <MDXTag name="h2" components={components}>{`Example Extensions`}</MDXTag>
      <MDXTag name="h3" components={components}>{`Container`}</MDXTag>
      <MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{
          "className": "language-jsx"
        }}>{`const Container = props =>
  <Box
    {...props}
    mx='auto'
    css={{
      maxWidth: '1024px'
    }}
  />
`}</MDXTag></MDXTag>
      <MDXTag name="h3" components={components}>{`Badge`}</MDXTag>
      <MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{
          "className": "language-jsx"
        }}>{`const Badge = props =>
  <Card
    color='white'
    bg='blue'
    {...props}
    px={3}
    py={1}
    borderRadius={9999}
    css={{
      display: 'inline-block'
    }}
  />
`}</MDXTag></MDXTag>
      <MDXTag name="h3" components={components}>{`Avatar`}</MDXTag>
      <MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{
          "className": "language-jsx"
        }}>{`const Avatar = props =>
  <Image
    width={48}
    height={48}
    borderRadius={9999}
    {...props}
  />
`}</MDXTag></MDXTag>
      <MDXTag name="h3" components={components}>{`BlockLink`}</MDXTag>
      <MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{
          "className": "language-jsx"
        }}>{`const BlockLink = props =>
  <Link
    color='inherit'
    {...props}
    css={{
      display: 'block',
      textDecoration: 'none'
    }}
  />
`}</MDXTag></MDXTag>
      <MDXTag name="h3" components={components}>{`NavLink`}</MDXTag>
      <MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{
          "className": "language-jsx"
        }}>{`const NavLink = props =>
  <Link
    px={2}
    py={1}
    color='inherit'
    {...props}
    css={{
      display: 'block',
      fontWeight: 'bold',
    }}
  />
`}</MDXTag></MDXTag>
      <MDXTag name="h3" components={components}>{`Embed`}</MDXTag>
      <MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{
          "className": "language-jsx"
        }}>{`const Embed = props =>
  <Box
    {...props}
    width={1}
    css={{
      height: 0,
      paddingBottom: (9 / 16) + '%',
      position: 'relative',
      overflow: 'hidden',
      '& > iframe': {
        position: 'absolute',
        width: '100%',
        height: '100%',
        top: 0,
        bottom: 0,
        left: 0,
        border: 0
      }
    }}
  />
`}</MDXTag></MDXTag>
      <MDXTag name="h3" components={components}>{`Pre`}</MDXTag>
      <MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{
          "className": "language-jsx"
        }}>{`const Pre = props =>
  <Text
    {...props}
    as='pre'
    fontFamily='Menlo, monospace'
    p={2}
    bg='lightgray'
  />
`}</MDXTag></MDXTag>
      <MDXTag name="h3" components={components}>{`Fixed`}</MDXTag>
      <MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{
          "className": "language-jsx"
        }}>{`const Fixed = props =>
  <Box
    {...props}
    css={{
      position: 'fixed'
    }}
  />
`}</MDXTag></MDXTag>
      <MDXTag name="h3" components={components}>{`Divider`}</MDXTag>
      <MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{
          "className": "language-jsx"
        }}>{`const Divider = props =>
  <Box
    {...props}
    as='hr'
    bg='gray'
    css={{
      border: 0,
      height: 1
    }}
  />
`}</MDXTag></MDXTag>
      <MDXTag name="h3" components={components}>{`Caps`}</MDXTag>
      <MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{
          "className": "language-jsx"
        }}>{`const Caps = props =>
  <Text
    fontSize={1}
    {...props}
    css={{
      textTransform: 'uppercase',
      letterSpacing: '0.2em'
    }}
  />
`}</MDXTag></MDXTag>
      <MDXTag name="h3" components={components}>{`Toolbar`}</MDXTag>
      <MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{
          "className": "language-jsx"
        }}>{`const Toolbar = props =>
  <Flex
    px={2}
    color='white'
    bg='black'
    alignItems='center'
    {...props}
  />
`}</MDXTag></MDXTag>
           </MDXTag>;
  }

}
MDXContent.isMDXComponent = true;
export const _frontmatter = {};
      